import { Calendars } from '@benzinga/calendars/definitions';
import type { CalendarDataI } from '@benzinga/calendars';
import { PageType } from '@benzinga/seo';
import type { MetaProps, StructuredDataI } from '@benzinga/seo';

export const CALENDARS_WITH_TABS: Record<string, string> = {
  earnings: '/earnings',
  dividends: '/dividends',
  fda: '/fda-calendar',
};

export const getStructuredData = (): StructuredDataI => ({
  keywords: ['Calendar'],
});

export function getCalendarMetaTitle(calendarData: CalendarDataI, currentTab?: string | number, prefix?: string) {
  if (!currentTab || !calendarData?.routes || !calendarData?.routes[currentTab]) return calendarData?.title;
  const baseTitle = calendarData?.routes[currentTab]?.title || calendarData?.title;
  return prefix ? `${prefix} ${baseTitle}` : baseTitle;
}

function getCalendarMetaDescription(calendarData: CalendarDataI, currentTab?: string | number) {
  const routeDesc =
    currentTab && calendarData?.routes?.[currentTab]
      ? calendarData.routes[currentTab].description || calendarData.description
      : calendarData?.description;
  return routeDesc || calendarData?.seo?.description || '';
}

export function getCalendarData(calendarSlug: string): CalendarDataI | null {
  return Calendars?.[calendarSlug] ?? null;
}

export function getCalendarBreadcrumbName(fullTitle: string | undefined, fallback: string): string {
  if (!fullTitle?.trim()) return fallback;
  return (
    fullTitle
      .replace(/\s+\| .*$/, '')
      .replace(/\s+as of .*$/i, '')
      .trim() || fallback
  );
}

export function getCalendarMeta(
  calendar?: CalendarDataI | null,
  calendarSlug?: string,
  calendarTab?: string | number,
  isNotAccessibleForFree?: boolean,
): MetaProps {
  const basePath =
    calendarSlug && CALENDARS_WITH_TABS[calendarSlug]
      ? CALENDARS_WITH_TABS[calendarSlug]
      : `/calendars/${calendarSlug}`;
  let canonical = calendar?.seo?.canonical || `https://www.benzinga.com${basePath}`;
  if (typeof calendarTab === 'string' && !!calendarTab.trim()) {
    canonical += `/${calendarTab}`;
  }
  const calendarMetaDescription = calendar ? getCalendarMetaDescription(calendar, calendarTab) : '';
  return {
    author: 'Benzinga',
    canonical,
    dateCreated: calendar?.calendarDate ? calendar.calendarDate.toISOString() : '2010-01-01T00:00:00Z',
    dateUpdated: calendar?.calendarDate ? calendar.calendarDate.toISOString() : '2010-01-01T00:00:00Z',
    description: calendarMetaDescription ?? '',
    dimensions: { contentType: PageType.Calendar },
    image:
      'https://cdn.benzinga.com/files/imagecache/bz2_opengraph_meta_image_400x300/sites/all/themes/bz2/images/bz-icon.png',
    isNotAccessibleForFree,
    ogDescription: calendarMetaDescription ?? null,
    pageType: PageType.Calendar,
    structuredData: getStructuredData(),
    title: calendar ? getCalendarMetaTitle(calendar, calendarTab) : 'Benzinga Calendar',
    twitterDescription: calendarMetaDescription ?? null,
  };
}
